import { CommonModule } from '@angular/common';
import {
    Component, ChangeDetectionStrategy, input, TemplateRef, contentChild, output, booleanAttribute, ChangeDetectorRef, model,
    numberAttribute, inject
} from '@angular/core';

import { Observable } from 'rxjs';

import { TreeItem, TreeItemBase } from '@models/base/helper-types';

@Component({
    selector: '[dm-tree]',
    templateUrl: './dm-tree.component.html',
    styleUrl: './dm-tree.component.less',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class DmTreeComponent<T extends TreeItemBase> {

    itemTpl             = contentChild.required('itemTpl', { read: TemplateRef });
    childrenFooterTpl   = contentChild('childrenFooterTpl', { read: TemplateRef });

    items               = model<TreeItem<T>[] | undefined>(undefined, { alias: 'dmTreeItems' });
    expanded            = model<{ [id: string | number]: boolean }>({}, { alias: 'dmTreeExpanded' });
    async               = input<boolean, unknown>(false, { alias: 'dmTreeAsync', transform: booleanAttribute });
    hasChildrenFn       = input<(item: TreeItem<T>) => boolean>(undefined, { alias: 'dmTreeHasChildrenFn' });
    loadChildrenFn      = input<(item: TreeItem<T>) => Observable<TreeItem<T>[]>>(undefined, { alias: 'dmTreeLoadChildrenFn' });
    disabledNodes       = input<{ [id: string]: boolean }>({}, { alias: 'dmTreeDisabledNodes' });
    levelLimit          = input<number, unknown>(100, { alias: 'dmTreeLevelLimit', transform: numberAttribute });
    foldTooltip         = input<string>('', { alias: 'dmTreeFoldTooltip' });
    wrapperClassesFn    = input<(item: TreeItem<T>) => string | string[] | Set<string> | { [klass: string]: any } | null | undefined>(undefined, { alias: 'dmTreeWrapperClassesFn' });
    expandOnClick       = input<boolean, unknown>(false, { alias: 'dmTreeExpandOnClick', transform: booleanAttribute });

    itemClick           = output<{ e: MouseEvent, item: TreeItem<T> }>({ alias: 'dmTreeItemClick' });
    itemContextMenu     = output<{ e: MouseEvent, item: TreeItem<T> }>({ alias: 'dmTreeItemContextmenu' });
    itemLoadChildren    = output<TreeItem<T>>({ alias: 'dmTreeItemLoadChildren' });

    loading: { [id: string | number]: boolean } = {};

    _cdr    = inject(ChangeDetectorRef);

    toggleExpanded(node: TreeItem<T>): void {
        if (!node.id) {
            return;
        }
        const expanded = this.expanded();
        if (expanded[node.id] || node.children || !this.async()) {
            expanded[node.id] = !expanded[node.id];
            this._cdr.detectChanges();
        }
        else if (this.async() && !this.loading[node.id]) {
            this.expandNode(node);
        }
        this._cdr.markForCheck();
    }

    expandNode(node: TreeItem<T>): void {
        if (!node.id) {
            return;
        }
        const expanded = this.expanded();
        const lc = this.loadChildrenFn();
        if (lc) {
            this.loading[node.id] = true;
            this._cdr.markForCheck();
            lc(node).subscribe({
                next: res => {
                    if (node.id) {
                        delete this.loading[node.id];
                        node.children = res;
                        this.items.update(v => v ? [...v]: v);
                        expanded[node.id] = true;
                        this._cdr.markForCheck();
                    }
                }
            });
        }
        else {
            this.loading[node.id] = true;
            this.itemLoadChildren.emit(node);
        }
    }

    onItemClick(e: MouseEvent, node: TreeItem<T>) {
        if (!node.id) {
            return;
        }
        const hfc = this.hasChildrenFn();
        if (this.expandOnClick() && (!!node.children || (this.async() && (!hfc || hfc(node))))) {
            if (node.children) {
                const expanded = this.expanded();
                expanded[node.id] = true;
            }
            else {
                this.expandNode(node);
            }
        }
        this.itemClick.emit({ e, item: node });
        this._cdr.markForCheck();
    }

}